import { getBreakdownReportQueryOptions } from "@packages/api-client/hooks/equipmentController";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { requireReachableRoute } from "@/lib/offline/routeGuard";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/equipment/breakdown-report/$reportId",
)({
	// Reviewing submitted reports is desk work — online-only; only the create
	// form queues offline.
	staticData: { offline: "online-only" },
	beforeLoad: () => requireReachableRoute("online-only"),
	loader: async ({ context, params }) => {
		await context.queryClient.ensureQueryData(
			getBreakdownReportQueryOptions(params.orgId, params.reportId),
		);
	},
	component: BreakdownReportDetailPage,
});

function BreakdownReportDetailPage() {
	const { orgId, reportId } = Route.useParams();
	const { t } = useTranslation();

	const { data: report } = useSuspenseQuery(
		getBreakdownReportQueryOptions(orgId, reportId),
	);

	if (!report) {
		return (
			<div className="flex h-full items-center justify-center">
				<p className="text-base-content/70">
					{t("equipment.breakdown.notFound")}
				</p>
			</div>
		);
	}

	const severityColors: Record<string, string> = {
		CRITICAL: "bg-error text-error-content",
		HIGH: "bg-warning text-warning-content",
		MEDIUM: "bg-info text-info-content",
		LOW: "bg-base-200 text-base-content",
	};

	return (
		<div className="flex flex-col gap-4 max-w-lg mx-auto w-full">
			<PageHeader
				title={t("equipment.breakdown.reportDetail")}
				showBackButton
			/>

			<Card>
				<CardContent className="pt-5 space-y-4">
					{/* Equipment Info */}
					{report.equipment && (
						<div>
							<p className="text-sm text-muted-foreground">
								{t("equipment.breakdown.equipment")}
							</p>
							<Link
								to="/app/org/$orgId/equipment/fleet/$equipmentId"
								params={{
									orgId,
									equipmentId: report.equipment.id,
								}}
								className="font-medium text-primary hover:underline"
							>
								{report.equipment.name} ({report.equipment.assetId})
							</Link>
						</div>
					)}

					{/* Severity */}
					<div>
						<p className="text-sm text-muted-foreground mb-1">
							{t("equipment.breakdown.severity")}
						</p>
						<Badge className={severityColors[report.severity] ?? ""}>
							{t(
								`equipment.breakdown.severityOptions.${report.severity}`,
								report.severity,
							)}
						</Badge>
					</div>

					{/* Description */}
					{report.description && (
						<div>
							<p className="text-sm text-muted-foreground">
								{t("equipment.breakdown.description")}
							</p>
							<p className="text-sm">{report.description}</p>
						</div>
					)}

					{/* Location */}
					{report.locationOnMachine && (
						<div>
							<p className="text-sm text-muted-foreground">
								{t("equipment.breakdown.locationOnMachine")}
							</p>
							<p className="text-sm">
								{t(
									`equipment.breakdown.locations.${report.locationOnMachine}`,
									report.locationOnMachine,
								)}
							</p>
						</div>
					)}

					{/* Linked Task */}
					{report.task && (
						<div>
							<p className="text-sm text-muted-foreground">
								{t("equipment.breakdown.linkedTask")}
							</p>
							<p className="text-sm font-medium">
								{report.task.title ?? report.task.id}
							</p>
						</div>
					)}

					{/* Reporter */}
					{report.reportedBy && (
						<div>
							<p className="text-sm text-muted-foreground">
								{t("equipment.breakdown.reportedBy")}
							</p>
							<p className="text-sm">
								{[report.reportedBy.firstName, report.reportedBy.lastName]
									.filter(Boolean)
									.join(" ") || report.reportedBy.id}
							</p>
						</div>
					)}

					{/* Date */}
					<div>
						<p className="text-sm text-muted-foreground">
							{t("common.createdAt")}
						</p>
						<p className="text-sm">
							{new Date(report.createdAt).toLocaleString()}
						</p>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
